import { CLARIFY_ITEM_ID_PATTERN, type FieldErrorCode } from './fieldErrorCodes';
import type { FieldValue } from './types';

/** Mirrors scada-items.ts on the server; the ingest reads the same entry shape. */
export const SCADA_ITEM_SIGNALS = ['feed_rate_kg_min', 'dose_size_kg'] as const;
export const SCADA_ITEMS_MAX = 500;
export const SCADA_FEED_LINE_MAX = 255;
/** The longest list text the server accepts in a save, so a longer list fails here first. */
export const SCADA_ITEMS_TEXT_MAX = 60_000;

type Parsed = { ok: true; value: unknown } | { ok: false };

function parseJson(text: string): Parsed {
    try {
        return { ok: true, value: JSON.parse(text) };
    } catch {
        return { ok: false };
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFeedLine(value: unknown): boolean {
    if (value === undefined || value === null) return true;
    return typeof value === 'string' && value.trim().length <= SCADA_FEED_LINE_MAX;
}

/** The trimmed item id of an entry the ingest would accept, or null; other keys are ignored. */
function entryItemId(entry: unknown): string | null {
    if (!isRecord(entry) || typeof entry.item_id !== 'string') return null;
    const itemId = entry.item_id.trim();
    const signalOk = SCADA_ITEM_SIGNALS.some((signal) => signal === entry.signal);
    return CLARIFY_ITEM_ID_PATTERN.test(itemId) && signalOk && isFeedLine(entry.feed_line)
        ? itemId
        : null;
}

/** The ingest skips a pen's whole list on one malformed entry or a repeated id. */
export function validateScadaItems(text: string): FieldErrorCode | null {
    if (text.length > SCADA_ITEMS_TEXT_MAX) return 'tooLong';
    const parsed = parseJson(text);
    if (!parsed.ok) return 'invalidJson';
    if (!Array.isArray(parsed.value)) return 'scadaItems';
    if (parsed.value.length > SCADA_ITEMS_MAX) return 'tooMany';
    const seen = new Set<string>();
    for (const entry of parsed.value) {
        const itemId = entryItemId(entry);
        if (itemId === null || seen.has(itemId)) return 'scadaItems';
        seen.add(itemId);
    }
    return null;
}

/** The number of entries when the value is a JSON array, well formed or not, else 0. */
export function scadaItemCount(value: FieldValue): number {
    if (typeof value !== 'string') return 0;
    const parsed = parseJson(value);
    return parsed.ok && Array.isArray(parsed.value) ? parsed.value.length : 0;
}

/** The trimmed item ids of a valid list; a malformed list has its own error and none. */
export function scadaItemIds(value: FieldValue): string[] {
    if (typeof value !== 'string' || value.trim() === '' || validateScadaItems(value) !== null)
        return [];
    const parsed = parseJson(value);
    if (!parsed.ok || !Array.isArray(parsed.value)) return [];
    return parsed.value.flatMap((entry) => entryItemId(entry) ?? []);
}

/** The key order the editor and the server write an entry in. */
const ENTRY_KEYS = ['item_id', 'signal', 'feed_line'];

/**
 * The list with each entry's keys in the written order, any other key after them
 * alphabetically, so the same list compares equal however its text was ordered. Text that
 * is not a JSON array comes back as it is, for validation to name.
 */
export function canonicalScadaText(text: string): string {
    const parsed = parseJson(text);
    if (!parsed.ok || !Array.isArray(parsed.value)) return text;
    return JSON.stringify(
        parsed.value.map((entry: unknown) => {
            if (!isRecord(entry)) return entry;
            const others = Object.keys(entry)
                .filter((key) => !ENTRY_KEYS.includes(key))
                .sort();
            const ordered: Record<string, unknown> = {};
            for (const key of [...ENTRY_KEYS, ...others])
                if (key in entry) ordered[key] = entry[key];
            return ordered;
        })
    );
}
